import { Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { ExerciseType } from '@/api/exercises'
import { ClozeEditor, DragGapsEditor, DropdownGapsEditor, SentenceBuilderEditor } from './GapEditors'
import { ChoiceEditor, PictureEditor, TrueFalseEditor } from './ChoiceEditors'
import { GroupingEditor, MatchingEditor, OrderingEditor } from './OrderEditors'
import { ErrorCorrectionEditor, OpenAnswerEditor, OralAnswerEditor, TranslationEditor } from './TextEditors'
import { CrosswordEditor, ListeningEditor } from './MediaEditors'
import { DictationEditor } from './DictationEditor'
import type { ThemeWord } from '../themeWords'
import type {
  ChoiceContent,
  CrosswordContent,
  DictationContent,
  DropdownGapsContent,
  ErrorCorrectionContent,
  GapsContent,
  GroupingContent,
  ListeningContent,
  MatchingContent,
  OpenAnswerContent,
  OralAnswerContent,
  OrderingContent,
  PictureContent,
  SentenceBuilderContent,
  TranslationContent,
  TrueFalseContent,
} from '../contentTypes'

/**
 * Диспетчер редакторов содержимого: по типу задания выбирает свой редактор.
 *
 * Универсальной формы под все восемнадцать типов не существует — «текст с
 * пропусками» и «кроссворд» не сводятся к одному набору полей, и попытка
 * свести их даёт форму, в которой половина полей всегда лишняя.
 */

type Props = {
  type: ExerciseType
  content: Record<string, unknown>
  onChange: (content: Record<string, unknown>) => void
  disabled?: boolean
  themeWords?: ThemeWord[]
  /** id сохранённого задания: нужен диктанту, чтобы озвучить текст на сервере */
  exerciseId?: number | null
  /**
   * Контур с серверной озвучкой аудирования (банк теста уровня). Гасит
   * предупреждение «автоматической озвучки пока нет» — в этом контуре она есть.
   */
  systemTts?: boolean
}

export function ExerciseContentEditor({
  type,
  content,
  onChange,
  disabled,
  themeWords,
  exerciseId,
  systemTts,
}: Props) {
  const { t } = useTranslation()
  const change = (next: unknown) => onChange(next as Record<string, unknown>)

  switch (type) {
    case 'drag_gaps':
      return <DragGapsEditor content={content as unknown as GapsContent} onChange={change} disabled={disabled} />
    case 'cloze':
      return <ClozeEditor content={content as unknown as GapsContent} onChange={change} disabled={disabled} />
    case 'dropdown_gaps':
      return (
        <DropdownGapsEditor content={content as unknown as DropdownGapsContent} onChange={change} disabled={disabled} />
      )
    case 'choice':
      return <ChoiceEditor content={content as unknown as ChoiceContent} onChange={change} disabled={disabled} />
    case 'true_false':
      return <TrueFalseEditor content={content as unknown as TrueFalseContent} onChange={change} disabled={disabled} />
    case 'picture':
      return <PictureEditor content={content as unknown as PictureContent} onChange={change} disabled={disabled} />
    case 'matching':
      return <MatchingEditor content={content as unknown as MatchingContent} onChange={change} disabled={disabled} />
    case 'sentence_builder':
      return (
        <SentenceBuilderEditor
          content={content as unknown as SentenceBuilderContent}
          onChange={change}
          disabled={disabled}
        />
      )
    case 'ordering':
      return <OrderingEditor content={content as unknown as OrderingContent} onChange={change} disabled={disabled} />
    case 'grouping':
      return <GroupingEditor content={content as unknown as GroupingContent} onChange={change} disabled={disabled} />
    case 'error_correction':
      return (
        <ErrorCorrectionEditor
          content={content as unknown as ErrorCorrectionContent}
          onChange={change}
          disabled={disabled}
        />
      )
    case 'translation':
      return (
        <TranslationEditor content={content as unknown as TranslationContent} onChange={change} disabled={disabled} />
      )
    case 'open_answer':
      return (
        <OpenAnswerEditor content={content as unknown as OpenAnswerContent} onChange={change} disabled={disabled} />
      )
    case 'oral_answer':
      return (
        <OralAnswerEditor content={content as unknown as OralAnswerContent} onChange={change} disabled={disabled} />
      )
    case 'crossword':
      return (
        <CrosswordEditor
          content={content as unknown as CrosswordContent}
          onChange={change}
          disabled={disabled}
          themeWords={themeWords}
        />
      )
    case 'listening':
      return (
        <ListeningEditor
          content={content as unknown as ListeningContent}
          onChange={change}
          disabled={disabled}
          systemTts={systemTts}
        />
      )
    case 'dictation':
      return (
        <DictationEditor
          content={content as unknown as DictationContent}
          onChange={change}
          disabled={disabled}
          exerciseId={exerciseId}
        />
      )
    default:
      return <Alert severity="warning">{t('exercises.editor.unknownType')}</Alert>
  }
}
